// Instrument definitions used by the client-portal assessment tools.
// Each instrument has: items, response scale, scoring function, interpretation bands.

export const PHQ9_ITEMS = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling or staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
  "Trouble concentrating on things, such as reading the newspaper or watching television",
  "Moving or speaking so slowly that other people could have noticed. Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual",
  "Thoughts that you would be better off dead, or of hurting yourself in some way",
];

export const GAD7_ITEMS = [
  "Feeling nervous, anxious, or on edge",
  "Not being able to stop or control worrying",
  "Worrying too much about different things",
  "Trouble relaxing",
  "Being so restless that it is hard to sit still",
  "Becoming easily annoyed or irritable",
  "Feeling afraid, as if something awful might happen",
];

export const FOUR_POINT_SCALE = [
  { value: 0, label: "Not at all" },
  { value: 1, label: "Several days" },
  { value: 2, label: "More than half the days" },
  { value: 3, label: "Nearly every day" },
];

export const ORS_ITEMS = [
  { id: "individual", label: "Individually", low: "Low", high: "High", hint: "Personal well-being" },
  { id: "interpersonal", label: "Interpersonally", low: "Low", high: "High", hint: "Family, close relationships" },
  { id: "social", label: "Socially", low: "Low", high: "High", hint: "Work, school, friendships" },
  { id: "overall", label: "Overall", low: "Low", high: "High", hint: "General sense of well-being" },
];

export const SRS_ITEMS = [
  { id: "relationship", label: "Relationship", low: "I did not feel heard, understood, and respected", high: "I felt heard, understood, and respected" },
  { id: "goals", label: "Goals and Topics", low: "We did not work on or talk about what I wanted to work on", high: "We worked on and talked about what I wanted to work on" },
  { id: "approach", label: "Approach or Method", low: "The therapist's approach is not a good fit for me", high: "The therapist's approach is a good fit for me" },
  { id: "overall", label: "Overall", low: "There was something missing in the session today", high: "Overall, today's session was right for me" },
];

export const ASAM_DIMENSIONS_TOOL = [
  { n: 1, title: "Intoxication, Withdrawal, and Addiction Medications", prompt: "Current withdrawal risk, medication needs, prior withdrawal history." },
  { n: 2, title: "Biomedical Conditions", prompt: "Physical health that affects treatment — pregnancy, chronic pain, hepatitis, diabetes." },
  { n: 3, title: "Psychiatric, Cognitive, and Emotional Conditions", prompt: "Co-occurring conditions, cognitive impairment, current suicide or violence risk." },
  { n: 4, title: "Substance Use / Addictive Behavior — Related Risks", prompt: "Behavioral risks, readiness-to-change context, service-planning considerations." },
  { n: 5, title: "Recovery Environment — Interpersonal", prompt: "Family, peers, social network, SIMOR-relevant relationships." },
  { n: 6, title: "Recovery Environment — Community, Cultural, Social, Structural", prompt: "Housing, employment, legal, transportation, culture, discrimination." },
];

export const ASAM_RISK_SCALE = [
  { value: 0, label: "0 — No risk", detail: "No current problem" },
  { value: 1, label: "1 — Minimal", detail: "Mild issues, self-manageable" },
  { value: 2, label: "2 — Moderate", detail: "Requires attention but not immediate care" },
  { value: 3, label: "3 — Significant", detail: "Requires clinical intervention" },
  { value: 4, label: "4 — Severe / imminent danger", detail: "Requires immediate care" },
];

// -- CHIME Domain Audit ------------------------------------------------------
export const CHIME_DOMAINS = [
  { id: "connectedness", label: "Connectedness", items: [
    "Patient has at least one non-clinical recovery-supportive relationship.",
    "Patient can name people they turn to during a craving or crisis.",
    "Group participation shows movement from using-group to recovery-group identity (SIMOR).",
    "Family / natural supports engaged in treatment planning.",
  ]},
  { id: "hope", label: "Hope", items: [
    "Patient articulates at least one specific future goal (30 / 90 / 365 days).",
    "Patient can point to prior success or protective factor.",
    "Clinician language reinforces possibility, not pathology.",
    "Setbacks framed as data, not failure.",
  ]},
  { id: "identity", label: "Identity", items: [
    "Patient uses language of the recovering-self more than the using-self.",
    "Roles beyond 'patient' are being reclaimed (parent, worker, artist, athlete).",
    "Cultural / spiritual identity honored in treatment plan.",
    "Discharge plan preserves emerging identity across LOC transitions.",
  ]},
  { id: "meaning", label: "Meaning", items: [
    "Patient names purpose beyond abstinence.",
    "Values-based work integrated in individual therapy.",
    "Service, work, or creative contribution scheduled in aftercare.",
    "Spiritual / existential concerns addressed if patient invites them.",
  ]},
  { id: "empowerment", label: "Empowerment", items: [
    "Treatment plan written in patient's own words.",
    "Patient chose at least one intervention modality.",
    "Documented patient-driven change in the last 30 days.",
    "Self-advocacy skills practiced (medication questions, LOC transitions).",
  ]},
];

export const CHIME_ITEM_SCALE = [
  { value: 0, label: "Absent" },
  { value: 1, label: "Emerging" },
  { value: 2, label: "Established" },
];

export function interpretCHIME(domainScores) {
  const totals = Object.values(domainScores).map(Number);
  const overall = totals.reduce((a, b) => a + b, 0);
  const maxPossible = CHIME_DOMAINS.length * 4 * 2; // 5 domains × 4 items × max 2
  const pct = Math.round((overall / maxPossible) * 100);
  if (pct >= 80) return { band: "Established across CHIME", detail: "Treatment plan is delivering measurable recovery process; sustain and prepare for LOC step-down." };
  if (pct >= 60) return { band: "Emerging with gaps", detail: "Most domains progressing; address the two lowest-scoring domains in next treatment plan review." };
  if (pct >= 40) return { band: "Uneven progress", detail: "Meaningful movement in some domains; several require targeted interventions before step-down." };
  return { band: "Early recovery", detail: "Foundational work still in progress. Do not step down LOC until at least three domains reach the Emerging band." };
}

// -- Framework Interaction Map -----------------------------------------------
export const FRAMEWORK_MAP_ITEMS = [
  { id: "asam", label: "ASAM 4th Ed.", usage: "LOC decisions and transitions" },
  { id: "narr", label: "NARR", usage: "Recovery housing operations" },
  { id: "simor", label: "SIMOR", usage: "Recovery-identity groupwork" },
  { id: "4e", label: "4E Cognition", usage: "Milieu, embodied, environmental cues" },
  { id: "chime", label: "CHIME", usage: "Treatment plan domains" },
  { id: "rosc", label: "ROSC", usage: "Care coordination + community supports" },
  { id: "fit", label: "FIT", usage: "ORS / SRS every session" },
  { id: "mbc", label: "MBC", usage: "PHQ-9, GAD-7, etc." },
  { id: "dp", label: "Deliberate Practice", usage: "Monthly clinician skill work" },
];

export function interpretFrameworkMap(inPlay) {
  const active = Object.values(inPlay).filter(Boolean).length;
  const pct = Math.round((active / FRAMEWORK_MAP_ITEMS.length) * 100);
  if (pct >= 78) return { band: `${active}/9 — Fully integrated`, detail: "The Continuum is active in this case. Focus on fidelity, not adoption." };
  if (pct >= 55) return { band: `${active}/9 — Partially integrated`, detail: "Prioritize the two missing frameworks in the next treatment plan review." };
  return { band: `${active}/9 — Framework debt`, detail: "Multiple non-negotiables are unaddressed. Escalate to the program director." };
}

// -- Staffing Calculator -----------------------------------------------------
// Ratios: patients : clinician. Conservative anchors from ASAM / NARR / SAMHSA guidance.
export const STAFFING_LOCS = [
  { id: "sober", label: "Recovery Housing (NARR I–IV)",
    ratios: { bht: 24, case_manager: 30, therapist: 40, nurse: 0, medical: 0 } },
  { id: "op", label: "Outpatient (1.0 / 1.5 / 1.7)",
    ratios: { bht: 40, case_manager: 40, therapist: 25, nurse: 0, medical: 200 } },
  { id: "iop", label: "IOP / PHP (2.x)",
    ratios: { bht: 20, case_manager: 25, therapist: 12, nurse: 40, medical: 60 } },
  { id: "res", label: "Residential (3.1 / 3.3 / 3.5)",
    ratios: { bht: 8, case_manager: 20, therapist: 12, nurse: 25, medical: 40 } },
  { id: "mm", label: "Medically Monitored (3.7)",
    ratios: { bht: 6, case_manager: 15, therapist: 10, nurse: 8, medical: 20 } },
  { id: "inpatient", label: "Medically Managed Inpatient (4.0)",
    ratios: { bht: 4, case_manager: 12, therapist: 10, nurse: 4, medical: 12 } },
];
export const STAFFING_ROLES = [
  { id: "bht", label: "Behavioral Health Tech" },
  { id: "case_manager", label: "Case Manager" },
  { id: "therapist", label: "Therapist" },
  { id: "nurse", label: "Nursing" },
  { id: "medical", label: "Medical (MD/DO/PMHNP/PA)" },
];

// -- Screening Battery Scorer ------------------------------------------------
export const SCREENING_INPUTS = [
  { id: "phq9", label: "PHQ-9 total", max: 27, band: (n) => n >= 15 ? "high" : n >= 10 ? "moderate" : n >= 5 ? "mild" : "minimal" },
  { id: "gad7", label: "GAD-7 total", max: 21, band: (n) => n >= 15 ? "severe" : n >= 10 ? "moderate" : n >= 5 ? "mild" : "minimal" },
  { id: "audit", label: "AUDIT (alcohol) total", max: 40, band: (n) => n >= 20 ? "possible dependence" : n >= 16 ? "high-risk" : n >= 8 ? "hazardous" : "low-risk" },
  { id: "dast", label: "DAST-10 total", max: 10, band: (n) => n >= 6 ? "substantial" : n >= 3 ? "moderate" : n >= 1 ? "low" : "no problem" },
  { id: "pcl5", label: "PCL-5 total", max: 80, band: (n) => n >= 33 ? "probable PTSD" : n >= 20 ? "clinical concern" : "sub-threshold" },
];

// -- Discharge Readiness -----------------------------------------------------
export const DISCHARGE_ITEMS = [
  { id: "dim5", text: "ASAM Dimension 5 (Interpersonal) rated 0–1 at last review." },
  { id: "dim6", text: "ASAM Dimension 6 (Community, Cultural, Social, Structural) rated 0–1." },
  { id: "phqgad", text: "PHQ-9 and GAD-7 trends stable or improved over last two weeks." },
  { id: "recovery_capital", text: "BARC-10 or ARC re-administered at discharge and score ≥ baseline." },
  { id: "housing", text: "Confirmed post-discharge housing (address, phone, contact person)." },
  { id: "employment", text: "Employment, school, or structured daytime activity documented." },
  { id: "aftercare", text: "Aftercare LOC scheduled within 7 days; first appointment on the calendar." },
  { id: "warm_handoff", text: "Warm handoff scheduled with receiving clinician (call or video)." },
  { id: "medication", text: "Medication reconciliation completed; 30-day supply or refill plan in place." },
  { id: "peer", text: "Peer / alumni contact identified and connected." },
  { id: "crisis_plan", text: "Crisis / relapse plan documented with named contacts and steps." },
  { id: "chime_documented", text: "CHIME progress documented and reviewed with patient." },
];

// -- SAFE-T ------------------------------------------------------------------
export const SAFET_STEPS = [
  { id: "risk_factors", label: "Step 1 — Risk factors",
    items: [
      "Prior suicide attempt",
      "Current substance use / intoxication",
      "Recent psychiatric hospitalization",
      "Recent loss (relationship, job, housing, custody)",
      "Family history of suicide",
      "Chronic pain or serious illness",
      "Access to lethal means",
      "Impulsivity or aggression",
    ] },
  { id: "protective", label: "Step 2 — Protective factors",
    items: [
      "Reasons for living identified",
      "Engaged in treatment",
      "Responsible for children or pets",
      "Positive social supports",
      "Religious / spiritual faith",
      "Access to medical / mental health care",
    ] },
  { id: "inquiry", label: "Step 3 — Suicide inquiry",
    items: [
      "Ideation present (thoughts, wishes, preoccupation)",
      "Plan formed (method, when, where)",
      "Intent (subjective expectation and desire to die)",
      "Behavior (rehearsal, giving things away, prior attempts)",
    ] },
];

export const SAFET_LEVELS = [
  { value: "low", label: "Low", detail: "Modifiable risk factors; strong protective factors; outpatient with safety planning." },
  { value: "moderate", label: "Moderate", detail: "Multiple risk factors; limited protective factors; consider IOP/PHP + safety planning + means restriction." },
  { value: "high", label: "High", detail: "Persistent ideation with plan/intent, or significant protective-factor deficit; consider ED evaluation and hospitalization." },
];

// -- Mental Status Exam ------------------------------------------------------
export const MSE_FIELDS = [
  { id: "appearance", label: "Appearance", options: ["Well-groomed", "Casually dressed", "Disheveled", "Poor hygiene", "Unusual"] },
  { id: "behavior", label: "Behavior / attitude", options: ["Cooperative", "Guarded", "Agitated", "Withdrawn", "Hostile"] },
  { id: "speech", label: "Speech", options: ["Normal rate/volume", "Pressured", "Slowed", "Slurred", "Poverty of speech"] },
  { id: "mood", label: "Mood (stated)", options: ["Euthymic", "Depressed", "Anxious", "Irritable", "Euphoric"] },
  { id: "affect", label: "Affect (observed)", options: ["Congruent / full range", "Constricted", "Blunted", "Flat", "Labile"] },
  { id: "thought_process", label: "Thought process", options: ["Linear / goal-directed", "Tangential", "Circumstantial", "Loose associations", "Flight of ideas"] },
  { id: "thought_content", label: "Thought content", options: ["No SI/HI/AH/VH", "Passive SI", "Active SI without plan", "Delusions", "Paranoia", "AH/VH present"] },
  { id: "cognition", label: "Cognition", options: ["Grossly intact", "Mild impairment", "Moderate impairment", "Severe impairment"] },
  { id: "insight", label: "Insight", options: ["Good", "Fair", "Limited", "Poor"] },
  { id: "judgment", label: "Judgment", options: ["Good", "Fair", "Limited", "Poor"] },
];

// -- Cognitive Screen (SLUMS-inspired, 30 pts) -------------------------------
export const COGNITIVE_ITEMS = [
  { id: "day", label: "Orientation — what day of the week is it?", max: 1 },
  { id: "year", label: "Orientation — what year is it?", max: 1 },
  { id: "state", label: "Orientation — what state are we in?", max: 1 },
  { id: "recall_immediate", label: "Immediate recall — repeat 3 unrelated words", max: 3 },
  { id: "math_change", label: "Math — how much change from a $20 bill after $8.50?", max: 2 },
  { id: "animals", label: "Fluency — name as many animals as you can in one minute (≥15 = 3)", max: 3 },
  { id: "recall_delayed", label: "Delayed recall — the 3 words from earlier", max: 5 },
  { id: "digit_span", label: "Digit span — 87 backward, 649 backward, 8537 backward", max: 3 },
  { id: "spelling", label: "Attention — spell 'WORLD' backward", max: 5 },
  { id: "clock", label: "Clock drawing — 11:10 with all numbers", max: 4 },
  { id: "story", label: "Story recall — name of the woman, place, why (2 pts each)", max: 2 },
];

export function interpretCognitive(total, education = "hs") {
  // Simplified interpretation — SLUMS uses education-based cutoffs.
  if (education === "hs") {
    if (total >= 27) return { band: "Normal", detail: "No cognitive impairment indicated." };
    if (total >= 21) return { band: "Mild neurocognitive disorder", detail: "Follow-up recommended; consider referral for formal neurocognitive assessment." };
    return { band: "Dementia range", detail: "Refer for full neurocognitive evaluation." };
  }
  if (total >= 25) return { band: "Normal", detail: "No cognitive impairment indicated." };
  if (total >= 20) return { band: "Mild neurocognitive disorder", detail: "Follow-up recommended; consider referral for formal neurocognitive assessment." };
  return { band: "Dementia range", detail: "Refer for full neurocognitive evaluation." };
}

// -- Reading Level (Flesch-Kincaid) ------------------------------------------
export function fleschKincaidGrade(text) {
  if (!text || !text.trim()) return null;
  const sentences = (text.match(/[.!?]+/g) || []).length || 1;
  const words = text.trim().split(/\s+/).filter(Boolean);
  const wordCount = words.length || 1;
  // Approx syllable counting per word
  const syllables = words.reduce((sum, w) => sum + countSyllables(w), 0) || 1;
  const grade = 0.39 * (wordCount / sentences) + 11.8 * (syllables / wordCount) - 15.59;
  const readingEase = 206.835 - 1.015 * (wordCount / sentences) - 84.6 * (syllables / wordCount);
  return {
    grade: Math.round(grade * 10) / 10,
    ease: Math.round(readingEase * 10) / 10,
    words: wordCount,
    sentences,
  };
}

function countSyllables(word) {
  word = (word || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!word) return 0;
  if (word.length <= 3) return 1;
  const cleaned = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "").replace(/^y/, "");
  const groups = cleaned.match(/[aeiouy]{1,2}/g);
  return groups ? groups.length : 1;
}

export function interpretReadingLevel(g) {
  if (g == null) return { band: "—", detail: "Enter text to analyze." };
  if (g <= 6) return { band: `Grade ${g}`, detail: "At or below 6th-grade level. Meets SAMHSA / plain-language target for behavioral health materials." };
  if (g <= 8) return { band: `Grade ${g}`, detail: "6th–8th grade. Acceptable for general adult patient materials." };
  if (g <= 12) return { band: `Grade ${g}`, detail: "9th–12th grade. Consider simplifying; a portion of patients will struggle." };
  return { band: `Grade ${g}`, detail: "College level. Rewrite before giving to patients; target grade 6–8." };
}

// -- Tools registry ----------------------------------------------------------
export function interpretORS(total) {
  if (total < 25) return { band: "Clinical range", detail: "Below the adult cutoff. Well-being distress consistent with those seeking services." };
  return { band: "Non-clinical range", detail: "Above the adult cutoff. Consider whether presenting concerns are appropriate for this level of care." };
}
export function interpretSRS(total) {
  if (total < 36) return { band: "Alliance concern", detail: "Below the alliance cutoff. Discuss what was missing and adjust the approach next session." };
  return { band: "Strong alliance", detail: "Alliance is reported as strong. Continue current approach; watch for changes." };
}
export function interpretPHQ9(total) {
  if (total <= 4) return { band: "Minimal", detail: "No treatment change indicated. Continue monitoring." };
  if (total <= 9) return { band: "Mild", detail: "Watchful waiting; repeat PHQ-9 at next visit." };
  if (total <= 14) return { band: "Moderate", detail: "Treatment plan, considering counseling, follow-up, or pharmacotherapy." };
  if (total <= 19) return { band: "Moderately severe", detail: "Active treatment with pharmacotherapy and/or psychotherapy." };
  return { band: "Severe", detail: "Immediate initiation of pharmacotherapy and/or psychotherapy; expedited referral to a mental health specialist." };
}
export function interpretGAD7(total) {
  if (total <= 4) return { band: "Minimal", detail: "None or minimal anxiety." };
  if (total <= 9) return { band: "Mild", detail: "Watchful waiting; repeat GAD-7 at next visit." };
  if (total <= 14) return { band: "Moderate", detail: "Possible clinically significant anxiety. Further evaluation recommended." };
  return { band: "Severe", detail: "Active treatment (psychotherapy and/or pharmacotherapy) recommended." };
}
export function interpretASAM(scores) {
  const values = Object.values(scores || {}).map(Number);
  const max = Math.max(0, ...values.filter((n) => !Number.isNaN(n)));
  if (max >= 4) return { band: "Level 4.0 — Medically managed inpatient", detail: "Imminent-danger risk in one or more dimensions." };
  if (max >= 3) return { band: "Level 3.5 – 3.7 — Residential or medically monitored", detail: "Significant risk requiring 24-hour clinical support." };
  if (max === 2) return { band: "Level 2.1 – 2.5 — IOP / PHP", detail: "Moderate risk; structured day treatment appropriate." };
  if (max === 1) return { band: "Level 1.0 – 1.7 — Outpatient", detail: "Minimal risk manageable in outpatient care." };
  return { band: "Level 1.0 or below", detail: "No current risk indicators; monitor and reassess as clinically indicated." };
}

export const INSTRUMENTS = {
  phq9: { label: "PHQ-9", tag: "Depression", href: "/portal/tools/phq9" },
  gad7: { label: "GAD-7", tag: "Anxiety", href: "/portal/tools/gad7" },
  ors: { label: "ORS", tag: "Outcome · FIT", href: "/portal/tools/ors" },
  srs: { label: "SRS", tag: "Alliance · FIT", href: "/portal/tools/srs" },
  asam: { label: "ASAM Dimension Scorer", tag: "Level of care", href: "/portal/tools/asam" },
  chime: { label: "CHIME Domain Audit", tag: "Treatment plan", href: "/portal/tools/chime" },
  framework_map: { label: "Framework Interaction Map", tag: "Fidelity", href: "/portal/tools/framework-map" },
  staffing: { label: "Staffing Calculator", tag: "Operations", href: "/portal/tools/staffing" },
  screening_battery: { label: "Screening Battery Scorer", tag: "Intake", href: "/portal/tools/screening" },
  discharge_readiness: { label: "Discharge Readiness Checker", tag: "Transitions", href: "/portal/tools/discharge" },
  safet: { label: "SAFE-T Suicide Risk", tag: "Safety", href: "/portal/tools/safet" },
  mse: { label: "Mental Status Exam", tag: "Clinical", href: "/portal/tools/mse" },
  cognitive: { label: "Cognitive Screen", tag: "Neurocog", href: "/portal/tools/cognitive" },
  reading_level: { label: "Reading Level", tag: "Materials", href: "/portal/tools/reading-level" },
};
